import { formatCurrency } from '@/lib/format';

import type { Decimal } from './contracts';

/**
 * What the catalogue says when the API sent no price for the reference date. It is a sentence,
 * not a dash: a buyer who sees "—" assumes the screen is still loading, and one who sees
 * `R$ 0,00` assumes the product is free. Neither is an answer.
 */
export const ABSENT_PRICE_LABEL = 'Preço não publicado';

/**
 * Coerces a decimal that crossed the wire as a JSON string into a number, **once**, at the
 * boundary — the same job `toFactor` does for unit conversions.
 *
 * Returns `null` for every shape that is not a number: `null`, `undefined`, `''`, `'abc'`, `NaN`.
 * That `null` is the whole point. `Number(null)` is `0` and `Number('')` is `0`, so the obvious
 * spelling of this function turns an absent price into a free product, which is the defect this
 * module exists to prevent. Every caller must branch on `null` rather than defaulting it.
 */
export function toAmount(value: Decimal | null | undefined): number | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * pt-BR money, or the absent-price sentence. `currency` is accepted and checked rather than
 * ignored: the formatter is hard-wired to BRL (ADR-0013 — the platform charges in reais), so a
 * response denominated in anything else must not be re-labelled as reais silently.
 */
export function formatPrice(value: Decimal | null | undefined, currency?: string | null): string {
  const amount = toAmount(value);
  if (amount === null) {
    return ABSENT_PRICE_LABEL;
  }

  if (currency != null && currency !== '' && currency !== 'BRL') {
    // Shown verbatim rather than run through the BRL formatter: `US$ 12,00` rendered as
    // `R$ 12,00` is a wrong number, not a formatting nit.
    return `${currency} ${amount.toFixed(2)}`;
  }

  return formatCurrency(amount);
}

/** `18,5%` — a spread or an observed cost variation, in the same pt-BR decimal separator. */
export function formatPercent(value: Decimal | null | undefined): string | null {
  const amount = toAmount(value);
  if (amount === null) {
    return null;
  }

  return `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(amount)}%`;
}
